import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Upload, X } from "lucide-react";
import { img, uploadImage, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const useUploader = (onUrls) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      onUrls(urls);
      toast.success(`${urls.length} file(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return { ref, busy, onChange };
};

export const UploadButton = ({ onUrls, multiple, testId, label = "Upload", accept = "image/*" }) => {
  const { ref, busy, onChange } = useUploader(onUrls);
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} hidden onChange={onChange} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} className="btn-outline !py-2" data-testid={testId}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {label}
      </button>
    </>
  );
};

export const ImageManager = ({ images, onChange }) => {
  const [url, setUrl] = useState("");
  const move = (i, d) => {
    const next = [...images];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div data-testid="image-manager">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {images.map((src, i) => (
          <div key={src + i} className="group relative overflow-hidden rounded-lg border border-clay" data-testid={`image-manager-item-${i}`}>
            <img src={img(src)} alt="" className="aspect-[4/3] w-full object-cover" />
            {i === 0 && <span className="absolute left-2 top-2 rounded-full bg-forest px-2 py-0.5 text-[10px] font-bold uppercase text-white">Cover</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-white/90 p-1">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30" aria-label="Move left"><ArrowLeft className="h-4 w-4" /></button>
              <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} className="p-1 text-destructive" aria-label="Remove" data-testid={`image-manager-remove-${i}`}><X className="h-4 w-4" /></button>
              <button type="button" disabled={i === images.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30" aria-label="Move right"><ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <UploadButton multiple onUrls={(u) => onChange([...images, ...u])} testId="image-manager-upload" label="Upload photos" />
        <div className="flex flex-1 gap-2">
          <Input placeholder="…or paste an image URL" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="image-manager-url" />
          <button type="button" className="btn-outline !py-2" onClick={() => { if (url) { onChange([...images, url]); setUrl(""); } }} data-testid="image-manager-add-url">Add</button>
        </div>
      </div>
      <p className="mt-2 text-xs text-moss">The first photo is used as the cover image.</p>
    </div>
  );
};

export const SingleImage = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-4">
    {value ? <img src={img(value)} alt="" className="h-20 w-28 rounded-lg object-cover" /> : <div className="h-20 w-28 rounded-lg bg-clay" />}
    <UploadButton onUrls={(u) => onChange(u[0])} testId={testId} label="Upload image" />
  </div>
);
